import React, { ComponentType, useEffect, useState } from 'react';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';

interface WithCellEventListenerProps {
  cell: ICell;
  className?: string;
  [key: string]: any;
}

interface EnhancedComponentProps extends WithCellEventListenerProps {
  value?: any;
  backgroundColor?: string;
  liveStyle?: React.CSSProperties;
}

/** A detached copy has no methods, so paints live on the workbook cell this copy came from. */
const resolveLiveCell = (cell: ICell, isCopy: boolean): ICell => {
  if (!isCopy) {
    return cell;
  }
  const key = cell.totalKey ?? cell.key;
  return (key ? (cell.sheet?.cells?.[key] as ICell | undefined) : undefined) ?? cell;
};

/** Empty live style so a copy does not keep a highlight the workbook cell already cleared. */
const NO_STYLE: React.CSSProperties = {};

// eslint-disable-next-line react/display-name
const withCellEventListener = <P extends WithCellEventListenerProps>(
  WrappedComponent: ComponentType<P & EnhancedComponentProps>
) =>
    function WithCellEventListener(props: P) {
      const { cell, className } = props;
      const isCopy = typeof cell.getFullKey !== 'function';
      const liveCell = resolveLiveCell(cell, isCopy);
      const fullKey = liveCell.getFullKey ? liveCell.getFullKey() : undefined;
      const [value, setValue] = useState<any>();
      const [cellClassName, setCellClassName] = useState<string | undefined>(className);
      const [cellBackgroundColor, setCellBackgroundColor] = useState<string | undefined>();
      // Copies read live style at render so a cleared highlight does not stick.
      const liveStyle = isCopy ? ((liveCell.style ?? NO_STYLE) as React.CSSProperties) : undefined;

      useEffect(() => {
        setCellClassName(className);
      }, [className]);

      useEffect(() => {
        const getValueHandler = () => {
          setValue(liveCell.value);
          const tmpClassName = className ?? '';
          const tmpCellClassName = liveCell.className ?? '';
          const cellClassesSet = new Set([...tmpCellClassName.split(' '), ...tmpClassName.split(' ')]);
          setCellClassName(Array.from(cellClassesSet).join(' '));
          setCellBackgroundColor(liveCell.backgroundColor);
        };

        if (fullKey) {
          document.addEventListener(fullKey, getValueHandler);
          return () => document.removeEventListener(fullKey, getValueHandler);
        }
        return undefined;
      }, [fullKey, liveCell, className]);

      const enhancedProps = {
        ...props,
        value,
        className: cellClassName,
        backgroundColor: cellBackgroundColor,
        liveStyle,
      } as P & EnhancedComponentProps;

      return React.createElement(WrappedComponent, enhancedProps);
    };

export default withCellEventListener;
